import React from 'react';
import { hungLightProportions } from '../../3d/geometry/proceduralRenderers/proceduralSpecs';
import { InlineSVGProjection } from '../projectionSvg';
import { genericHungLight } from '@shared-lib/lighting/genericCeilingLights';

/**
 * Elevation drawing for a HUNG ceiling light — a pendant or a chandelier.
 *
 * Part projection, part schematic, and the split is the whole design:
 *
 * Every band is the fixture's real outline, cropped out of its own edge
 * projection: a projection's viewBox is the model's bounding box and each
 * `materialRegions` band says where it sits in that box, so `sliceTop` /
 * `sliceHeight` cut the part out of the artwork (checked against all 28 hung
 * fixtures in the catalog; canopy at the top in every one).
 *
 * The STEM is the one band drawn at a length the model did not choose — a user
 * sets it per placement (`stemLengthInches`) and catalog pendants are modelled
 * at full cord. Its slice is therefore STRETCHED to the cut length, which is
 * exact rather than approximate for what the catalog holds: every stem band in
 * it is a uniform-section rod or cord (one `maxRadius`, `surfaceType: metal`),
 * and stretching a uniform section along its own axis maps parallel lines to
 * parallel lines. It is what the 3D scene does to the same band, which shifts
 * the stem's vertices instead of re-modelling it. A CHAIN would smear, and the
 * tell is links that grow with the drop.
 *
 * A generic (`proceduralStyle: 'pendant' | 'chandelier'`, no model) draws
 * its own bands (`@shared-lib/lighting/genericCeilingLights`), as the 3D scene
 * does. Without projections or regions it falls back to plain rectangles.
 *
 * The canopy sits at the TOP of the object's box, because that is where the
 * pipeline puts it: a pendant's box runs from the ceiling down
 * (`planKitchenPendants` seats `center.y = seatY - height / 2`) and the 3D
 * scene anchors the GLB the same way. A shortened cord therefore stops short
 * of the box's bottom, exactly as the fixture rises in the scene.
 */
export function HungLightSchematic({
  obj,
  catalogModel,
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const isSide = view === 'left' || view === 'right';
  const horizontal = isSide ? depth : width;
  const { bands } = hungLightProportions({
    width: horizontal,
    height,
    stemLengthInches: obj?.stemLengthInches,
    // A generic pendant / chandelier has no catalog model; its own bands are
    // the profile, the ones the 3D scene draws it from.
    regions:
      catalogModel?.materialRegions ||
      genericHungLight(obj?.proceduralStyle)?.regions,
  });

  // Radially symmetric, so any view's artwork reads correctly; prefer the one
  // asked for and fall back to the front.
  const projections = catalogModel?.edgeProjections;
  const url = (projections?.[view] || projections?.front)?.url || null;
  const pct = (v, total) => `${total > 0 ? (v / total) * 100 : 0}%`;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {bands.map((band, i) => {
        // A crop is a horizontal band of the WHOLE artwork, so it spans the
        // model's full width whatever the part inside it measures — the
        // artwork states that itself. Squeezing the slice into the band's own
        // width instead distorts it, by 2.6x on a fixture whose canopy is much
        // narrower than its shade. Only a DRAWN band is sized to its part.
        //
        // The box's HEIGHT is the band's drawn height, which for the stem is
        // the cut length rather than the model's: the slice stretches to fill
        // it. Nothing else is stretched — the other bands' boxes are their own
        // heights.
        const fromArtwork = !!url && band.sliceHeight > 0;
        const boxWidth = fromArtwork ? horizontal : band.width;
        const box = {
          position: 'absolute',
          left: pct((horizontal - boxWidth) / 2, horizontal),
          top: pct(band.y, height),
          width: pct(boxWidth, horizontal),
          height: pct(band.height, height),
        };
        if (fromArtwork) {
          return (
            <div key={i} style={box}>
              <InlineSVGProjection
                url={url}
                alt={`${band.semantic} projection`}
                crop={{ top: band.sliceTop, height: band.sliceHeight }}
                strokeWidthPx={strokeWidth}
                style={{ width: '100%', height: '100%', pointerEvents: 'none' }}
              />
            </div>
          );
        }
        return (
          <svg
            key={i}
            style={box}
            viewBox={`0 0 ${Math.max(band.width, 1e-6)} ${Math.max(band.height, 1e-6)}`}
            preserveAspectRatio="none"
            overflow="visible"
          >
            {band.width > 0 ? (
              <rect
                x={0}
                y={0}
                width={band.width}
                height={band.height}
                fill="none"
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                vectorEffect="non-scaling-stroke"
              />
            ) : (
              <line
                x1={0}
                y1={0}
                x2={0}
                y2={band.height}
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>
        );
      })}
    </div>
  );
}

HungLightSchematic.supportsSideViews = true;
HungLightSchematic.supportsBackView = true;
